import { usePage } from '@inertiajs/react';
import { DiscountCodeInput } from './DiscountCodeInput';
import { CartSummaryMobile } from './CartSummaryMobile';
import { formatPrice } from '../../utils/formatPrice';
import type { CartSubscriptionInfo, DiscountInfo } from '../../types/cart';

interface DiscountPreviewData {
  code: string;
  discount: number;
  discountPercentage: number;
  discountableItems: string[];
  discountInfo: DiscountInfo;
}

interface CartSummaryProps {
  subtotal: number;
  discountCode: string | null;
  discountAmount: number;
  discountError: string | null;
  discountInfo: DiscountInfo | null;
  deliveryPrice: number;
  total: number;
  allowsDiscountCode: boolean;
  isLoading: boolean;
  onApplyDiscount: (code: string) => Promise<void>;
  onPreviewDiscount: (code: string) => Promise<void>;
  onConfirmDiscount: () => Promise<void>;
  onCancelPreview: () => void;
  previewData: DiscountPreviewData | null;
  onClearDiscount: () => Promise<void>;
  checkoutUrl: string;
  canCheckout: boolean;
  itemCount: number;
  subscription?: CartSubscriptionInfo | null;
  replacementCreditDiscount?: number;
}

export function CartSummary(props: CartSummaryProps) {
  const {
    subtotal,
    discountCode,
    discountAmount,
    discountError,
    discountInfo,
    deliveryPrice,
    total,
    allowsDiscountCode,
    isLoading,
    onApplyDiscount,
    onPreviewDiscount,
    onConfirmDiscount,
    onCancelPreview,
    previewData,
    onClearDiscount,
    checkoutUrl,
    canCheckout,
    subscription,
    replacementCreditDiscount,
  } = props;

  return (
    <>
      {/* Mobile: collapsible bottom sheet */}
      <div className="md:hidden">
        <CartSummaryMobile
          subtotal={subtotal}
          discountCode={discountCode}
          discountAmount={discountAmount}
          discountError={discountError}
          discountInfo={discountInfo}
          deliveryPrice={deliveryPrice}
          total={total}
          allowsDiscountCode={allowsDiscountCode}
          isLoading={isLoading}
          onApplyDiscount={onApplyDiscount}
          onPreviewDiscount={onPreviewDiscount}
          onConfirmDiscount={onConfirmDiscount}
          onCancelPreview={onCancelPreview}
          previewData={previewData}
          onClearDiscount={onClearDiscount}
          checkoutUrl={checkoutUrl}
          canCheckout={canCheckout}
          subscription={subscription}
          replacementCreditDiscount={replacementCreditDiscount}
        />
      </div>

      {/* Desktop: sticky sidebar */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 p-5 shadow-sm sticky top-5">
        {subscription && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800">
            <span className="font-medium">{subscription.plan_name}</span>
            {' — '}
            {subscription.discount_percent}% de descuento
            {subscription.free_shipping && ' + envío gratis'}
          </div>
        )}

        {allowsDiscountCode && (
          <DiscountCodeInput
            initialCode={discountCode}
            discountAmount={discountAmount}
            discountError={discountError}
            discountInfo={discountInfo}
            isLoading={isLoading}
            onApplyDiscount={onApplyDiscount}
            onPreviewDiscount={onPreviewDiscount}
            onConfirmDiscount={onConfirmDiscount}
            onCancelPreview={onCancelPreview}
            previewData={previewData}
            onClearDiscount={onClearDiscount}
          />
        )}

        <div className="bg-gray-50 rounded-lg p-3.5 mb-4">
          <div className="flex justify-between text-sm text-gray-500 py-1.5">
            <span>Subtotal</span>
            <span>{formatPrice(subtotal)}</span>
          </div>

          {subscription && subscription.discount_amount > 0 && (
            <div className="flex justify-between text-sm text-green-600 py-1.5">
              <span>{subscription.plan_name} ({subscription.discount_percent}%)</span>
              <span>-{formatPrice(subscription.discount_amount)}</span>
            </div>
          )}

          {discountAmount > 0 && discountCode && !discountInfo?.is_free_delivery && (
            <div className="flex justify-between text-sm text-green-600 py-1.5">
              <span>{discountInfo?.display_name || `Cupón (${discountCode.toUpperCase()})`}</span>
              <span>-{formatPrice(discountAmount)}</span>
            </div>
          )}

          {(replacementCreditDiscount ?? 0) > 0 && (
            <div className="flex justify-between text-sm text-green-600 py-1.5">
              <span>Credito por reposicion</span>
              <span>-{formatPrice(replacementCreditDiscount!)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm py-1.5">
            <span className={discountInfo?.is_free_delivery ? 'text-green-600 font-medium' : 'text-gray-500'}>
              {discountInfo?.is_free_delivery ? '🚚 Envío gratis' : 'Envío'}
            </span>
            <span className={discountInfo?.is_free_delivery ? 'text-green-600' : 'text-gray-500'}>
              {discountInfo?.is_free_delivery ? (
                <>
                  <span className="line-through text-gray-400 mr-2">{formatPrice(deliveryPrice)}</span>
                  <span className="font-medium">Gratis</span>
                </>
              ) : deliveryPrice === 0 ? (
                <span className={subscription?.free_shipping ? 'text-green-600 font-medium' : ''}>Gratis</span>
              ) : (
                formatPrice(deliveryPrice)
              )}
            </span>
          </div>

          <div className="flex justify-between text-[17px] font-semibold text-gray-800 border-t border-gray-200 mt-2 pt-3">
            <span>Total</span>
            <span>{formatPrice(total)}</span>
          </div>
        </div>

        <a
          href={canCheckout ? checkoutUrl : '#'}
          className={`block w-full py-3.5 px-5 text-center no-underline text-base font-semibold rounded-lg transition-colors duration-150 ${
            canCheckout
              ? 'bg-green-600 text-white hover:bg-green-700 hover:text-white'
              : 'bg-gray-300 text-white cursor-not-allowed'
          }`}
          onClick={(e) => {
            if (!canCheckout) e.preventDefault();
          }}
        >
          Realizar Pedido
        </a>
        <AuthHint />
      </div>
    </>
  );
}

function AuthHint() {
  const { auth } = usePage<{ auth: { user: unknown | null } }>().props;
  if (auth?.user) return null;

  return (
    <p className="text-center text-xs text-gray-400 mt-2.5 mb-0">
      A continuación vas a iniciar sesión o crear una cuenta
    </p>
  );
}
